import { DateInput } from "../components/DateInput";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, exportError, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT, tr, LANG } from "../lib/i18n";
import { Download, FileText } from "lucide-react";
import { omr } from "../lib/showroomForms";
import { SlabEditor, toApiSlabs } from "../components/SlabEditor";

const PAY = { salary_commission: "com.salary_commission", commission_only: "com.commission_only" };

function ShowroomSlabs() {
  const { t } = useT();
  const [slabs, setSlabs] = useState(null);
  useEffect(() => { api.get("/commissions/slabs").then(({ data }) => setSlabs(data.slabs)).catch(() => setSlabs([])); }, []);
  if (slabs === null) return null;
  const save = async () => {
    try { const { data } = await api.put("/commissions/slabs", { slabs: toApiSlabs(slabs) }); setSlabs(data.slabs); toast.success(t("com.slabs_saved")); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="panel" style={{ padding: 20 }} data-testid="showroom-slabs">
      <div className="kpi-label mb-3">{t("com.showroom_slabs")}</div>
      <SlabEditor slabs={slabs} onChange={setSlabs} testid="showroom-slab" />
      <button className="btn-primary mt-3" onClick={save} data-testid="showroom-slabs-save">{tr("x.save")}</button>
    </div>
  );
}

const thisMonth = () => new Date().toISOString().slice(0, 7);

async function download(fmt, month) {
  try {
    const [y, m] = month.split("-").map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const { data } = await api.get(`/reports/salesperson_commission/${fmt}`, { params: { range: "custom", date_from: `${month}-01`, date_to: `${month}-${last}` }, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `salesperson-commission-${month}.${fmt === "excel" ? "xlsx" : "pdf"}`; document.body.appendChild(a); a.click(); a.remove();
  } catch (e) { toast.error(await exportError(e)); }
}

export default function CommissionsPage() {
  const { t } = useT();
  const { user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/commissions/report", { params: { month } }); setRows(data.rows); } catch (e) { toast.error(formatApiError(e)); }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  const markPaid = async (r) => {
    if (!window.confirm(`${t("com.mark_paid")}: ${r.name} · ${month}`)) return;
    try { await api.post("/commissions/mark-paid", { user_id: r.user_id, month }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="commissions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="kicker">{tr("x.finance")}</div>
          <h1 className="mt-2" style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-0.02em" }}>{t("com.page")}</h1>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <label className="kpi-label block">{t("com.month")}<DateInput data-testid="com-month" type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} className={LANG === "ar" ? "mt-1 flex" : "input mt-1 block"} /></label>
          <button data-testid="com-excel" className="btn-secondary" onClick={() => download("excel", month)}><Download size={14} /> Excel</button>
          <button data-testid="com-pdf" className="btn-primary" onClick={() => download("pdf", month)}><FileText size={14} /> PDF</button>
        </div>
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full dense-table text-sm" data-testid="com-table">
          <thead><tr className="text-start"><th className="p-3">{t("sf.salesperson")}</th><th>{t("com.pay_type")}</th><th>{t("com.slab")}</th><th>{t("com.sales_count")}</th><th>{t("com.total")}</th><th>{t("com.target")}</th><th>{t("com.paid")}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-t" data-testid={`com-row-${r.user_id}`}>
                <td className="p-3">{r.name}</td>
                <td>{PAY[r.pay_type] ? t(PAY[r.pay_type]) : "—"}</td>
                <td dir="ltr">{r.current_percent != null ? `${r.current_percent}%` : "—"}</td>
                <td>{r.sales_count}</td>
                <td><bdi>{PAY[r.pay_type] ? omr(r.commission_total) : "—"}</bdi>{r.reversals ? ` (${r.reversals} ${t("com.reversal")})` : ""}</td>
                <td dir="ltr">{r.target ? `${r.target.percent}%` : "—"}</td>
                <td>{r.paid ? t("com.paid_by", { name: `${r.paid_by} · ${String(r.paid_at).slice(0, 10)}` })
                  : (PAY[r.pay_type] && user?.role === "admin" ? <button data-testid={`com-mark-${r.user_id}`} className="h-10 px-3 rounded-lg border" onClick={() => markPaid(r)}>{t("com.mark_paid")}</button> : "—")}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[var(--gray-400)]" data-testid="com-empty">{tr("x.no_data")}</td></tr>}
          </tbody>
        </table>
      </div>
      {user?.role === "admin" && <ShowroomSlabs />}
    </div>
  );
}

export function CommissionCard() {
  const { t } = useT();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!PAY[user?.pay_type]) return;
    api.get("/commissions/me").then(({ data }) => setD(data)).catch(() => {});
  }, [user]);
  if (!PAY[user?.pay_type] || !d) return null;
  return (
    <>
    {d.target && <TargetCard tg={d.target} />}
    <div className="panel" style={{ padding: 20 }} data-testid="commission-card">
      <div className="kpi-label">{t("com.card")}</div>
      <div className="text-3xl font-semibold mt-1" dir="ltr" data-testid="commission-total">{omr(d.total)}</div>
      <div className="text-sm mt-2 flex flex-wrap gap-x-4 gap-y-1" data-testid="commission-slab-info">
        <span>{t("com.cars_this_month", { n: d.cars })}</span>
        <span>{t("com.current_rate", { p: d.current_percent })}{d.current_slab ? ` (${d.current_slab.from_cars}–${d.current_slab.to_cars ?? "+"})` : ""}</span>
        {d.cars_to_next_slab != null && <span data-testid="commission-next-slab">{t("com.to_next", { n: d.cars_to_next_slab, p: d.next_slab.percent })}</span>}
      </div>
      {d.items.length === 0 ? <div className="text-sm opacity-60 mt-2">{t("com.none")}</div> : (
        <ul className="mt-3 divide-y text-sm">
          {d.items.map((i, n) => (
            <li key={n} className="flex justify-between py-2" data-testid="commission-item">
              <span>{i.sale_reference} · {i.vehicle_label}{i.kind === "reversal" ? ` · ${t("com.reversal")}` : ""}</span>
              <span dir="ltr">{omr(i.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
    </>
  );
}

function TargetCard({ tg }) {
  const { t } = useT();
  return (
    <div className="panel" style={{ padding: 20, borderInlineStart: "4px solid var(--accent)", background: "var(--accent-100)" }} data-testid="target-card">
      <div className="kpi-label">{t("com.target_month")}</div>
      <div className="flex flex-wrap gap-6 mt-2 text-sm">
        {tg.cars && <div data-testid="target-cars"><b className="text-2xl" dir="ltr">{tg.achieved_cars} / {tg.cars}</b> {t("com.cars")}</div>}
        {tg.value && <div data-testid="target-value"><b className="text-2xl" dir="ltr">{omr(tg.achieved_value)}</b> / <bdi dir="ltr">{omr(tg.value)}</bdi></div>}
      </div>
      <div className="mt-3 h-2 rounded-full bg-white overflow-hidden" role="progressbar" aria-valuenow={tg.percent} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full" style={{ width: `${Math.min(100, tg.percent)}%`, background: "var(--accent)" }} />
      </div>
      <div className="text-sm mt-1" dir="ltr" data-testid="target-percent">{tg.percent}%</div>
    </div>
  );
}
